import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchAnaActivity, fetchAnaOverview } from '../../../services/ana-channels-api.js';

const CHANNELS = [
  ['global', 'Global'],
  ['instagram', 'Instagram'],
  ['facebook', 'Facebook'],
  ['whatsapp', 'WhatsApp'],
  ['email', 'Correo']
];

const CHANNEL_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  email: 'Correo',
  meta: 'Meta',
  unknown: 'Sin identificar'
};

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value || '—' : date.toLocaleString('es-CL');
}

function directionLabel(value) {
  if (value === 'inbound') return 'Entrada';
  if (value === 'outbound') return 'Salida';
  return 'Interno';
}

function providerLabel(value) {
  const provider = String(value || '').toLowerCase();
  if (provider === 'meta') return 'Meta directo';
  if (provider === 'kommo' || provider.includes('kommo')) return 'Kommo / CRM';
  if (provider === 'gmail') return 'Gmail';
  if (provider === 'manychat') return 'ManyChat';
  return value || '—';
}

function channelFromOrigin(value) {
  const origin = String(value || '').trim().toLowerCase();
  if (origin.includes('instagram') || origin === 'ig') return 'instagram';
  if (origin.includes('facebook') || origin.includes('messenger') || origin === 'fb') return 'facebook';
  if (origin.includes('whatsapp') || origin.includes('waba')) return 'whatsapp';
  return 'whatsapp';
}

function surfaceForChannel(channel) {
  if (channel === 'instagram') return 'instagram_dm';
  if (channel === 'facebook') return 'messenger';
  return 'whatsapp';
}

function diagnosticMetadata(event) {
  const metadata = event?.metadata && typeof event.metadata === 'object' ? event.metadata : {};
  return {
    ...metadata,
    trace: {
      id: event?.id || '',
      timestamp: event?.timestamp || '',
      channel: event?.channel || '',
      surface: event?.surface || '',
      direction: event?.direction || '',
      provider: event?.provider || '',
      eventType: event?.eventType || '',
      status: event?.status || '',
      sourceId: event?.sourceId || '',
      conversationId: event?.conversationId || '',
      userId: event?.userId || '',
      requestId: event?.requestId || '',
      sourceUrl: event?.sourceUrl || '',
      sourceContextAvailable: Boolean(event?.sourceContextAvailable),
      sourceContext: event?.sourceContext || null
    }
  };
}

function sourceContext(event) {
  const metadata = diagnosticMetadata(event);
  const context = event?.sourceContext || metadata?.sourceContext || {};
  const url = event?.sourceUrl || context?.sourceUrl || metadata?.sourceUrl || '';
  const available = Boolean(event?.sourceContextAvailable || context?.contextAvailable || url);
  return { context, url, available, metadata };
}

function normalizeKommoEvents(overview) {
  const channels = Array.isArray(overview?.channels) ? overview.channels : [];
  const whatsapp = channels.find((item) => item.channel === 'whatsapp');
  const recent = Array.isArray(whatsapp?.recentEvents) ? whatsapp.recentEvents : [];
  return recent.map((event, index) => {
    const timestamp = event.updatedAt || event.occurredAt || event.receivedAt || event.createdAt || event.dueAt || '';
    const rawDirection = String(event.direction || '').toLowerCase();
    const direction = rawDirection === 'incoming' ? 'inbound' : rawDirection === 'outgoing' ? 'outbound' : 'internal';
    const realChannel = channelFromOrigin(event.origin);
    const detail = event.text || event.reason || event.summary || event.detail || event.status || `Evento de ${CHANNEL_LABELS[realChannel] || realChannel} vía Kommo`;
    return {
      id: `kommo-${event.eventId || event.id || event.messageId || event.key || timestamp || index}`,
      timestamp,
      channel: realChannel,
      surface: surfaceForChannel(realChannel),
      direction,
      provider: 'kommo',
      eventType: event.messageType || event.status || 'response_supervisor',
      sourceId: event.messageId || event.eventId || event.id || '',
      conversationId: event.talkId ? String(event.talkId) : '',
      userId: event.contactId ? String(event.contactId) : '',
      summary: String(detail),
      status: event.status || '',
      requestId: event.ingress?.requestId || '',
      sourceUrl: event.sourceUrl || event.sourceContext?.sourceUrl || '',
      sourceContextAvailable: Boolean(event.sourceContextAvailable),
      sourceContext: event.sourceContext || null,
      metadata: {
        leadId: event.leadId ?? null,
        talkId: event.talkId ?? null,
        contactId: event.contactId ?? null,
        messageId: event.messageId ?? null,
        eventId: event.eventId ?? null,
        origin: event.origin || '',
        direction: event.direction || '',
        status: event.status || '',
        reason: event.reason || '',
        ingress: event.ingress || null,
        sourceContext: event.sourceContext || null,
        rawEvent: event
      }
    };
  });
}

function matchesQuery(event, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return [
    event.channel,
    event.surface,
    event.direction,
    event.provider,
    event.eventType,
    event.sourceId,
    event.conversationId,
    event.userId,
    event.summary,
    event.status,
    event.requestId,
    event.sourceUrl,
    event.metadata ? JSON.stringify(event.metadata) : ''
  ].filter(Boolean).join(' ').toLowerCase().includes(needle);
}

function SourceCell({ event }) {
  const source = sourceContext(event);
  return (
    <div style={{ display: 'grid', gap: 5, maxWidth: 360 }}>
      {source.available ? (
        source.url ? (
          <a href={source.url} target="_blank" rel="noreferrer" style={{ overflowWrap: 'anywhere' }}>
            Abrir publicación origen
          </a>
        ) : <span>Contexto de publicación disponible</span>
      ) : <span style={{ opacity: .6 }}>Sin contexto de publicación</span>}
      <details>
        <summary style={{ cursor: 'pointer', fontSize: 12, opacity: .84, fontWeight: 700 }}>Ver metadata / detalle técnico</summary>
        <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 11, maxHeight: 320, overflow: 'auto', padding: 8, borderRadius: 8, background: 'rgba(127,127,127,.08)' }}>
          {JSON.stringify(source.metadata, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export default function AnaActivityPage() {
  const params = useParams();
  const selected = CHANNELS.some(([id]) => id === params.channel) ? params.channel : 'global';
  const channel = selected === 'global' ? '' : selected;
  const [direction, setDirection] = useState('');
  const [query, setQuery] = useState('');
  const [events, setEvents] = useState([]);
  const [persistent, setPersistent] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const needsKommoOverview = ['global', 'instagram', 'facebook', 'whatsapp'].includes(selected);
      const shouldFetchAllPersistent = ['instagram', 'facebook', 'whatsapp'].includes(channel);
      const [activity, overview] = await Promise.all([
        fetchAnaActivity({
          channel: shouldFetchAllPersistent ? '' : channel,
          direction: shouldFetchAllPersistent ? '' : direction,
          query: shouldFetchAllPersistent ? '' : query,
          limit: 500
        }),
        needsKommoOverview ? fetchAnaOverview() : Promise.resolve(null)
      ]);
      const persistentEvents = Array.isArray(activity?.events) ? activity.events : [];
      const kommoEvents = normalizeKommoEvents(overview);
      const combined = [...persistentEvents, ...kommoEvents]
        .filter((event) => !channel || event.channel === channel)
        .filter((event) => !direction || event.direction === direction)
        .filter((event) => matchesQuery(event, query))
        .sort((a, b) => String(b.timestamp || '').localeCompare(String(a.timestamp || '')));
      setEvents(combined);
      setPersistent(activity?.persistent !== false);
    } catch (err) {
      setError(err && err.message ? err.message : 'No se pudo cargar la actividad de Ana.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [channel, direction]);

  const summary = useMemo(() => {
    const byChannel = events.reduce((acc, event) => {
      acc[event.channel] = (acc[event.channel] || 0) + 1;
      return acc;
    }, {});
    return {
      total: events.length,
      inbound: events.filter((event) => event.direction === 'inbound').length,
      outbound: events.filter((event) => event.direction === 'outbound').length,
      byChannel
    };
  }, [events]);

  return (
    <main style={{ display: 'grid', gap: 20, paddingBottom: 48, minWidth: 0 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <p className="analytics-eyebrow">Ana · trazabilidad</p>
          <h1 style={{ margin: '0 0 8px' }}>Actividad multicanal</h1>
          <p style={{ margin: 0, opacity: 0.75, maxWidth: 900, lineHeight: 1.55 }}>
            Acá podés ver por dónde entró cada evento, por qué proveedor pasó y, cuando Kommo lo entrega, el contexto de la publicación de origen.
          </p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading}>{loading ? 'Actualizando…' : 'Actualizar'}</button>
      </header>

      <nav aria-label="Canales de Ana" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {CHANNELS.map(([id, label]) => (
          <Link
            key={id}
            to={id === 'global' ? '/ana-actividad' : '/ana-actividad/' + id}
            style={{
              textDecoration: 'none', padding: '9px 13px', borderRadius: 999,
              border: '1px solid rgba(127,127,127,.28)', fontWeight: 700,
              background: selected === id ? 'rgba(127,127,127,.16)' : 'transparent', color: 'inherit'
            }}
          >{label}</Link>
        ))}
      </nav>

      {!persistent ? (
        <div role="status" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>
          El registro persistente de canales no está configurado. Kommo puede seguir mostrando la auditoría disponible del Supervisor por su canal real.
        </div>
      ) : null}
      {error ? <div role="alert" style={{ padding: 14, border: '1px solid currentColor', borderRadius: 12 }}>{error}</div> : null}

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        {[
          ['Eventos', summary.total], ['Entradas', summary.inbound], ['Salidas', summary.outbound],
          ['Instagram', summary.byChannel.instagram ?? 0], ['Facebook', summary.byChannel.facebook ?? 0],
          ['WhatsApp', summary.byChannel.whatsapp ?? 0], ['Correo', summary.byChannel.email ?? 0]
        ].map(([label, value]) => (
          <div key={label} style={{ padding: 15, border: '1px solid rgba(127,127,127,.24)', borderRadius: 14 }}>
            <div style={{ fontSize: 12, opacity: .66 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 5 }}>{value}</div>
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'minmax(240px,1fr) minmax(180px,260px) auto', gap: 10, alignItems: 'end' }}>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Buscar</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void load(); }} placeholder="post, reel, comentario, request id, proveedor…" />
        </label>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Dirección</span>
          <select value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="">Todas</option><option value="inbound">Entrada</option><option value="outbound">Salida</option><option value="internal">Interno</option>
          </select>
        </label>
        <button type="button" onClick={() => void load()}>Buscar</button>
      </section>

      <section style={{ border: '1px solid rgba(127,127,127,.24)', borderRadius: 16, overflow: 'hidden', minWidth: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1320 }}>
            <thead><tr>
              {['Hora', 'Canal', 'Dirección', 'Proveedor / camino', 'Superficie', 'Evento', 'Mensaje / detalle', 'Origen / publicación', 'Estado', 'ID / trazabilidad'].map((label) => (
                <th key={label} style={{ textAlign: 'left', padding: '12px 10px', fontSize: 12, opacity: .68, borderBottom: '1px solid rgba(127,127,127,.2)' }}>{label}</th>
              ))}
            </tr></thead>
            <tbody>
              {!loading && events.length === 0 ? <tr><td colSpan="10" style={{ padding: 22, opacity: .7 }}>Todavía no hay eventos para este filtro. Los nuevos eventos se irán registrando automáticamente.</td></tr> : null}
              {events.map((event) => (
                <tr key={event.id}>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)', whiteSpace: 'nowrap' }}>{formatDate(event.timestamp)}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)', fontWeight: 700 }}>{CHANNEL_LABELS[event.channel] || event.channel}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>{directionLabel(event.direction)}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>{providerLabel(event.provider)}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>{event.surface || '—'}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>{event.eventType || '—'}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)', maxWidth: 420, whiteSpace: 'pre-wrap' }}>{event.summary || '—'}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}><SourceCell event={event} /></td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)' }}>{event.status || '—'}</td>
                  <td style={{ padding: '12px 10px', borderBottom: '1px solid rgba(127,127,127,.12)', fontSize: 12, opacity: .72 }}>{event.sourceId || event.requestId || event.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section style={{ padding: 16, border: '1px solid rgba(127,127,127,.22)', borderRadius: 14, opacity: .82 }}>
        <strong>Cómo leer “Origen / publicación”</strong>
        <p style={{ margin: '8px 0 0', lineHeight: 1.55 }}>
          “Ver metadata / detalle técnico” aparece siempre. Aunque Kommo no entregue un permalink/post/reel/comentario, podés inspeccionar Talk, contacto, request id, origen, estado y cualquier dato técnico disponible para diagnosticar el camino de entrega.
        </p>
      </section>
    </main>
  );
}